/*
    Margin Trend (MPN-1530 §4.4).

    Same calculation as the Full Campaign panel's Margin (§4.1), re-bucketed:

        prorated budget for a bucket = total budget x (bucket days / total scheduled days)
        margin                       = (prorated - actual spend) / prorated

    Positive means the bucket underspent its share of the pace, negative means it ran hot.

    Computed here rather than on the server because both inputs already arrive together:
    spend-panels carries the budget, the flight, AND the daily spend. A third endpoint
    returning a third opinion about margin is exactly how the panel and the chart end up
    disagreeing.

    Reads spend-panels.daily_spend, NOT performance-by-day. Both are daily spend, but
    performance-by-day is narrowed by the Date Filter while this chart paces against the
    campaign: section 4.4 calls it "the same margin calculation" as section 4.1 and defines
    its Overall grain as the full campaign flight. Using the windowed series made Overall
    read 58% against the panel's 67% the moment anyone picked a range, which is the exact
    disagreement this file exists to avoid.

    "Overall" is therefore the whole flight as one bucket, and equals the panel's Margin
    by construction.
*/

export type MarginGrain = "weekly" | "monthly" | "overall"

export type MarginPoint = {
  label: string
  /** Fraction, e.g. 0.523 for +52.3%. Null when the bucket has nothing to prorate. */
  margin: number | null
  spend: number
  prorated: number
  days: number
}

type Flight = {
  totalBudget: number
  daysScheduled: number
}

function parseDay(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, m - 1, d)
}

/** Monday-anchored, matching Postgres date_trunc('week') used elsewhere in the stack. */
function weekStart(date: Date): Date {
  const d = new Date(date)
  const offset = (d.getDay() + 6) % 7
  d.setDate(d.getDate() - offset)
  return d
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function labelFor(grain: MarginGrain, bucketStart: Date): string {
  if (grain === "monthly") {
    return bucketStart.toLocaleDateString(undefined, { month: "short", year: "numeric" })
  }
  return bucketStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

/**
 * Margin per bucket at the requested grain.
 *
 * Every bucket prorates by the number of days IT contains, not by an equal share. A
 * campaign starting mid-week gives its first week three days of budget, not seven, and
 * calendar months are not the same length. Dividing the budget evenly across buckets
 * would make short buckets look wildly overspent.
 */
export function marginTrend(
  charts: any,
  grain: MarginGrain,
): MarginPoint[] {
  const panels = charts?.["spend-panels"] ?? {}
  const flight: Flight = {
    totalBudget: Number(panels.total_campaign_budget ?? 0),
    daysScheduled: Number(panels.days_scheduled ?? 0),
  }

  // Already one row per day over the full flight, so no per-network collapsing needed.
  const days: { day: string; spend: number }[] = (panels.daily_spend ?? []).map(
    (d: any) => ({ day: String(d.day), spend: Number(d.spend ?? 0) }),
  )
  if (days.length === 0 || !flight.totalBudget || !flight.daysScheduled) return []

  const perDayBudget = flight.totalBudget / flight.daysScheduled

  if (grain === "overall") {
    const spend = days.reduce((sum, d) => sum + d.spend, 0)
    const prorated = perDayBudget * days.length
    return [
      {
        label: "Overall",
        spend,
        prorated,
        days: days.length,
        margin: prorated ? (prorated - spend) / prorated : null,
      },
    ]
  }

  const buckets = new Map<string, { start: Date; spend: number; days: number }>()
  for (const day of days) {
    const date = parseDay(day.day)
    const start = grain === "weekly" ? weekStart(date) : monthStart(date)
    const key = start.toISOString().slice(0, 10)
    const bucket = buckets.get(key) ?? { start, spend: 0, days: 0 }
    bucket.spend += day.spend
    bucket.days += 1
    buckets.set(key, bucket)
  }

  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, bucket]) => {
      const prorated = perDayBudget * bucket.days
      return {
        label: labelFor(grain, bucket.start),
        spend: bucket.spend,
        prorated,
        days: bucket.days,
        margin: prorated ? (prorated - bucket.spend) / prorated : null,
      }
    })
}

export function formatMargin(value: number | null): string {
  if (value === null) return "—"
  const pct = value * 100
  // Signed, because the sign is the message: this is over or under pace, not a score.
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`
}
